import { useState, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import TrackCard from '../components/TrackCard'
import LoadingSpinner from '../components/LoadingSpinner'
import { analyzeTrack, getRecommendations } from '../services/api'
import { useMixerStore } from '../store/mixerStore'
import './RecommendationsPage.css'

function RecommendationsPage() {
  const { trackId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { setDeckA } = useMixerStore()

  const [sourceTrack, setSourceTrack] = useState(location.state?.track || null)
  const [analysis, setAnalysis] = useState(null)
  const [recommendations, setRecommendations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState({ completed: 0, total: 0, percentage: 0, currentTrack: '' })

  useEffect(() => {
    loadRecommendations()
  }, [trackId])

  const loadRecommendations = async () => {
    setLoading(true)
    setError(null)
    setProgress({ completed: 0, total: 0, percentage: 0, currentTrack: '' })

    try {
      // Analyze the source track with REAL audio analysis
      console.log('Analyzing source track with REAL Web Audio API...')
      const analysisData = await analyzeTrack(trackId, sourceTrack?.permalink_url)
      setAnalysis(analysisData)

      // Get recommendations with REAL compatibility scoring
      console.log('Getting recommendations with REAL compatibility scoring...')
      const recData = await getRecommendations(trackId, analysisData, (progressData) => {
        setProgress(progressData)
      })
      setRecommendations(recData)
    } catch (err) {
      setError('Failed to load recommendations. Please try again.')
      console.error('Recommendations error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handlePreviewBlend = (recommendedTrack, recommendedAnalysis) => {
    // Load source track into Deck A
    setDeckA({
      track: sourceTrack,
      analysis: analysis,
      cuePoint: 0
    })

    // Navigate to mixer with recommended track and ALL recommendations
    navigate('/mixer', {
      state: {
        deckBTrack: recommendedTrack,
        deckBAnalysis: recommendedAnalysis,
        recommendations: recommendations, // Pass all recommendations
        sourceTrack: sourceTrack,
        sourceAnalysis: analysis
      }
    })
  }

  if (loading) {
    return (
      <div className="recommendations-page loading">
        <LoadingSpinner size="large" />
        {progress.total > 0 ? (
          <div className="analysis-progress">
            <p className="progress-message">
              Analyzing candidate tracks: {progress.completed}/{progress.total}
            </p>
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
            <p className="progress-percentage">{progress.percentage}% complete</p>
            {progress.currentTrack && (
              <p className="current-track">
                {progress.failed ? '❌ Failed: ' : '🎵 '}
                {progress.currentTrack}
              </p>
            )}
          </div>
        ) : (
          <p>Analyzing source track and finding perfect blends...</p>
        )}
      </div>
    )
  }

  if (error) {
    return (
      <div className="recommendations-page error">
        <p className="error-message">{error}</p>
        <button onClick={() => navigate('/')}>Back to Search</button>
      </div>
    )
  }

  return (
    <div className="recommendations-page">
      <button
        className="back-button"
        onClick={() => navigate('/')}
        style={{ marginBottom: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
      >
        ← Back to Search
      </button>

      <div className="source-track-section">
        <h2>Source Track</h2>
        <div className="source-track-details">
          <div className="source-artwork">
            <img
              src={sourceTrack?.artwork_url || '/placeholder-artwork.png'}
              alt={sourceTrack?.title}
            />
          </div>
          <div className="source-info">
            <h3>{sourceTrack?.title}</h3>
            <p className="artist">{sourceTrack?.user?.username}</p>

            {analysis && (
              <div className="analysis-stats">
                <div className="stat-group">
                  <h4>Analysis Results</h4>
                  <div className="stats-grid">
                    <div className="stat">
                      <span className="label">BPM</span>
                      <span className="value">{analysis.bpm}</span>
                    </div>
                    <div className="stat">
                      <span className="label">Key</span>
                      <span className="value">{analysis.key}</span>
                    </div>
                    <div className="stat">
                      <span className="label">Energy</span>
                      <span className="value">{(analysis.energy * 100).toFixed(0)}%</span>
                    </div>
                    <div className="stat">
                      <span className="label">Vocals</span>
                      <span className="value">
                        {analysis.vocalScore > 0.7 ? 'High' : analysis.vocalScore > 0.3 ? 'Medium' : 'Low'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="stat-group">
                  <h4>Frequency Profile</h4>
                  <div className="frequency-bars">
                    <div className="freq-bar">
                      <div className="bar-label">Sub-Bass</div>
                      <div className="bar-track">
                        <div
                          className="bar-fill"
                          style={{ width: `${analysis.subBassEnergy * 100}%` }}
                        />
                      </div>
                      <div className="bar-value">{(analysis.subBassEnergy * 100).toFixed(0)}%</div>
                    </div>
                    <div className="freq-bar">
                      <div className="bar-label">Bass</div>
                      <div className="bar-track">
                        <div
                          className="bar-fill"
                          style={{ width: `${analysis.bassEnergy * 100}%` }}
                        />
                      </div>
                      <div className="bar-value">{(analysis.bassEnergy * 100).toFixed(0)}%</div>
                    </div>
                    <div className="freq-bar">
                      <div className="bar-label">Mids</div>
                      <div className="bar-track">
                        <div
                          className="bar-fill"
                          style={{ width: `${analysis.midEnergy * 100}%` }}
                        />
                      </div>
                      <div className="bar-value">{(analysis.midEnergy * 100).toFixed(0)}%</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="recommendations-section">
        <h2>Recommended Blends ({recommendations.length})</h2>
        <p className="section-subtitle">
          Tracks that match harmonically, rhythmically, and stylistically
        </p>

        {recommendations.length > 0 ? (
          <div className="recommendations-grid">
            {recommendations.map((rec) => (
              <TrackCard
                key={rec.track.id}
                track={rec.track}
                onAnalyze={() => handlePreviewBlend(rec.track, rec.analysis)}
                showCompatibility={true}
                compatibility={rec.compatibility}
                analysis={rec.analysis}
              />
            ))}
          </div>
        ) : (
          <div className="no-recommendations">
            <p>No compatible tracks found. Try searching for similar tracks manually.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default RecommendationsPage
